<template>
  <v-row>
    <v-col cols="12">
      <v-card class="vend-card" :class="{ 'vend-card--dialog': mode === 'dialog' }" flat>
        <!-- HEADER BAND (page mode only) -->
        <div v-if="mode === 'page'" class="vend-header pl-base-bg">
          <div class="vend-header-icon">
            <v-icon color="#fff" size="22">mdi-truck-plus</v-icon>
          </div>
          <div>
            <div class="vend-title">Create Vendor</div>
            <div class="vend-subtitle">Add a new vendor to your directory</div>
          </div>
        </div>

        <!-- FORM -->
        <v-form ref="form" v-model="valid" :class="mode === 'page' ? 'pa-6' : 'pa-0'">
          <!-- CONTACT DETAILS -->
          <div class="vend-section-label">Contact Details</div>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.name"
                label="Vendor Name"
                outlined
                dense
                :rules="[rules.required]"
                prepend-inner-icon="mdi-domain"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.email"
                label="Email"
                outlined
                dense
                :rules="[rules.email]"
                prepend-inner-icon="mdi-email"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.phone"
                label="Phone"
                outlined
                dense
                prepend-inner-icon="mdi-phone"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.gstin"
                label="GSTIN"
                outlined
                dense
                :rules="[rules.gstin]"
                prepend-inner-icon="mdi-file-document"
              />
            </v-col>
          </v-row>

          <!-- ADDRESS -->
          <div class="vend-section-label mt-2">Address</div>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.pincode"
                label="Pincode"
                outlined
                dense
                :rules="[rules.pincode]"
                :loading="pincodeLoading"
                maxlength="6"
                prepend-inner-icon="mdi-map-marker-radius"
                @input="onPincodeInput"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.city"
                label="City"
                outlined
                dense
                prepend-inner-icon="mdi-city"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.state"
                label="State"
                outlined
                dense
                prepend-inner-icon="mdi-map-marker"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.country"
                label="Country"
                outlined
                dense
                prepend-inner-icon="mdi-earth"
              />
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="form.address"
                label="Address"
                outlined
                dense
                rows="2"
                prepend-inner-icon="mdi-home"
              />
            </v-col>
          </v-row>

          <!-- ACTIONS -->
          <v-row class="mt-4" justify="end" dense>
            <v-col cols="6" sm="auto">
              <v-btn block color="secondary" @click="reset">Reset</v-btn>
            </v-col>

            <v-col v-if="!isViewOnly" cols="6" sm="auto">
              <v-btn
                block
                class="vend-save-btn"
                :loading="loading"
                @click="submit"
              >
                Save
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { mapActions } from "vuex";
import { lookupPincode, PINCODE_RULE } from "@/utils/pincode";

// Same structural GSTIN check as server/src/models/vendor.js's GSTIN_REGEX —
// duplicated client-side purely for immediate form feedback; the server
// validation is the actual trust boundary.
const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

export default {
  name: "CreateVendor",

  props: {
    mode: {
      type: String,
      default: "page", // "dialog" | "page"
    },
  },

  data() {
    return {
      valid: false,
      loading: false,
      pincodeLoading: false,
      pincodeDebounce: null,

      form: {
        name: "",
        email: "",
        phone: "",
        gstin: "",
        address: "",
        city: "",
        state: "",
        pincode: "",
        country: "",
      },

      rules: {
        required: (v) => !!v || "Required",
        email: (v) => !v || /.+@.+\..+/.test(v) || "Invalid email",
        pincode: PINCODE_RULE,
        gstin: (v) => !v || GSTIN_REGEX.test(v.toUpperCase()) || "Enter a valid 15-character GSTIN",
      },
    };
  },

  methods: {
    ...mapActions("vendors", ["createVendor"]),

    onPincodeInput(value) {
      clearTimeout(this.pincodeDebounce);
      if (!/^\d{6}$/.test(value)) return;

      this.pincodeDebounce = setTimeout(async () => {
        this.pincodeLoading = true;
        const result = await lookupPincode(value);
        this.pincodeLoading = false;

        if (!result) return;
        this.form.city = result.city || this.form.city;
        this.form.state = result.state || this.form.state;
        this.form.country = result.country || this.form.country;
      }, 400);
    },

    async submit() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;

      try {
        let result = await this.createVendor(this.form);

        this.$toast?.success("Vendor created successfully");

        if (this.mode === "dialog") {
          this.$emit("created", result);
        }

        if (this.mode === "page") {
          this.$router.push("/vendors");
        }
      } catch (err) {
        this.$toast?.error(
          err?.response?.data?.message || "Failed to create vendor",
        );
      } finally {
        this.loading = false;
      }
    },

    reset() {
      this.$refs.form.reset();
    },
  },
};
</script>

<style scoped>
.vend-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.vend-card--dialog {
  box-shadow: none;
  border-radius: 0;
}

.vend-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
}

.vend-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.vend-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.vend-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.vend-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 10px;
}

.vend-save-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}
</style>
